import type { IngestionSortColumn } from '@/models/ingestion.type';
import { trackEvent } from '@/shared/analytics/trackEvent';
import { downloadBlob, getFileName } from '@/shared/utils/download-utils';
import { Button, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

// Même limite que l'extension Directus `oru-export-ingestion`.
const MAX_EXPORT_INGESTIONS = 1000;
// Le CORS doit autoriser les credentials et exposer l'en-tête `Content-Disposition` (nom du fichier).
const EXPORT_URL = `${import.meta.env.VITE_DIRECTUS_URL}/oru-export-ingestion`;

type Props = {
  /** Ids des ingestions sélectionnées. */
  ingestionIds: number[];
  /** Colonnes de l'onglet « Synthèse », dans l'ordre affiché. */
  columns: IngestionSortColumn[];
};

/** Télécharge au format Excel la synthèse et le détail des anomalies des ingestions sélectionnées. */
const IngestionsExportButton = ({ ingestionIds, columns }: Props) => {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const count = ingestionIds.length;
  const tooMany = count > MAX_EXPORT_INGESTIONS;

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await fetch(EXPORT_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ingestionIds, columns }),
      });
      if (!response.ok) {
        throw new Error(`Export des ingestions en échec (${response.status})`);
      }
      downloadBlob(await response.blob(), getFileName(response, 'ingestions.xlsx'));
      trackEvent('ingestion-export-telecharger', { count, colonnes: columns.length });
    } catch {
      notifications.show({ color: 'red', message: t('admin.ingestions.export.error') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip
      label={t('admin.ingestions.export.tooMany', { max: MAX_EXPORT_INGESTIONS })}
      withArrow
      disabled={!tooMany}
    >
      {/* Un bouton désactivé n'émet pas d'événements souris : le `span` porte l'infobulle. */}
      <span>
        <Button
          leftSection={<IconFileSpreadsheet size={16} stroke={1.5} />}
          onClick={handleExport}
          loading={loading}
          disabled={tooMany || columns.length === 0}
        >
          {t('admin.ingestions.export.button', { count })}
        </Button>
      </span>
    </Tooltip>
  );
};

export default IngestionsExportButton;
